// AutomationWriteService.js

import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

const AUTOMATIONS_COLLECTION = collections.automations;
const DATABASES_COLLECTION = collections.collections;

const normalizeScopeValue = (value) => String(value || '').trim();

function buildAutomationPayload({ rule, userKey, baseKey, nodeType, existingData = null }) {
  const normalizedRule = rule && typeof rule === 'object' ? rule : null;
  const normalizedUserKey = normalizeScopeValue(userKey);
  const normalizedBaseKey = normalizeScopeValue(baseKey);
  const normalizedNodeType = normalizeScopeValue(nodeType);
  const normalizedRuleKey = normalizeScopeValue(normalizedRule?.key);

  if (!normalizedRule || !normalizedUserKey || !normalizedBaseKey || !normalizedNodeType || !normalizedRuleKey) {
    return null;
  }

  const now = Timestamp.now();
  const existingCreatedBy = normalizeScopeValue(existingData?.createdBy);

  return {
    ...normalizedRule,
    key: normalizedRuleKey,
    userKey: normalizedUserKey,
    createdBy: existingCreatedBy || normalizedUserKey,
    baseKey: normalizedBaseKey,
    scope: 'NODE_TYPE',
    nodeType: normalizedNodeType,
    dateCreated: existingData?.dateCreated || now,
    dateModified: now,
  };
}

export async function saveAutomationRule({ rule, userKey, baseKey, nodeType } = {}) {
  const normalizedRuleKey = normalizeScopeValue(rule?.key);
  if (!normalizedRuleKey || !userKey || !baseKey || !nodeType) return null;

  const ref = doc(db, AUTOMATIONS_COLLECTION, normalizedRuleKey);
  const existingSnap = await getDoc(ref);
  const existingData = existingSnap.exists() ? (existingSnap.data() || {}) : null;
  const payload = buildAutomationPayload({
    rule,
    userKey,
    baseKey,
    nodeType,
    existingData,
  });

  if (!payload) return null;

  await setDoc(ref, payload, { merge: false });
  return payload;
}

export async function deleteAutomationRuleByKey(ruleKey) {
  const normalizedRuleKey = normalizeScopeValue(ruleKey);
  if (!normalizedRuleKey) return;

  await deleteDoc(doc(db, AUTOMATIONS_COLLECTION, normalizedRuleKey));
}

export async function clearLegacyDatabaseAutomations({ baseKey, typeKey } = {}) {
  const normalizedBaseKey = normalizeScopeValue(baseKey);
  const normalizedTypeKey = normalizeScopeValue(typeKey);
  if (!normalizedBaseKey || !normalizedTypeKey) return 0;

  const snapshot = await getDocs(query(
    collection(db, DATABASES_COLLECTION),
    where('baseKey', '==', normalizedBaseKey),
    where('type', '==', normalizedTypeKey),
  ));

  if (snapshot.empty) return 0;

  const batch = writeBatch(db);
  const now = Timestamp.now();

  snapshot.docs.forEach((docSnap) => {
    batch.update(docSnap.ref, {
      automations: deleteField(),
      dateModified: now,
    });
  });

  try {
    await batch.commit();
  } catch (error) {
    // Legacy cleanup is best-effort. `_collections` updates require the doc owner
    // (userKey), so base admins may not clear embedded automations even
    // though `_automations` writes succeed.
    if (error?.code !== 'permission-denied') {
      throw error;
    }
    console.warn('[AutomationWriteService] Skipped legacy automations cleanup:', error);
    return 0;
  }

  return snapshot.size;
}
